import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import shieldQuestion from '../../../../../assets/shield_question.svg';
import styles from './MatchCard.module.scss';
import { useSelector } from 'react-redux';
import { getTranslation } from 'utils/getTranslation';
import { useSearchParams } from 'react-router-dom';

const MAX_TEAM_NAME = 15;
const MAX_DATE = 10;

const TeamRow = ({ name, flag, sourceType }) => (
  <div className={styles['match-card__team']}>
    <div className={styles['match-card__flag-wrap']}>
      <img
        src={flag || shieldQuestion}
        alt={name || 'Por definir'}
        className={styles['match-card__flag-img']}
        // style={{ width: flag ? 18 : 16, height: flag ? 12 : 13 }}
        onError={e => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = shieldQuestion;
        }}
      />
    </div>
    <span
      className={styles['match-card__team-name']}
      style={{
        color:
          sourceType === 'PLACEHOLDER' ? 'rgba(255, 255, 255, 0.60)' : '#fff',
      }}
    >
      {renderMaxLengthText(name, MAX_TEAM_NAME)}
    </span>
  </div>
);

TeamRow.propTypes = {
  name: PropTypes.string,
  flag: PropTypes.string,
  score: PropTypes.number,
  winner: PropTypes.bool,
};

TeamRow.defaultProps = {
  name: 'Por definir',
  flag: null,
  score: null,
  winner: false,
};

const formatMinute = progress => {
  let display = `${progress.minute}'`;
  if (progress.extraMinute) display += `+${progress.extraMinute}'`;
  if (progress.stoppageMinute) display += `+${progress.stoppageMinute}'`;
  return display;
};

const MatchCard = ({
  homeTeam,
  awayTeam,
  date,
  time,
  timezone,
  status,
  isBroadcastAvailable,
  progress,
  id,
}) => {
  const [, setSearchParams] = useSearchParams();
  const translations = useSelector(state => state.translations);
  const isLive = status === 'live';
  const isFinished = status === 'finished';
  const isPending = status === 'pending';

  const handleSelectLive = () => {
    if (!isLive) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);

      next.delete('screen');
      next.set('tab', 'fixtures');
      next.set('matchId', String(id));

      return next;
    });
  };

  return (
    <div
      className={classNames(
        styles['match-card'],
        styles[`match-card--${status}`],
      )}
      onClick={isLive ? handleSelectLive : undefined}
      role={isLive ? 'button' : undefined}
      tabIndex={isLive ? 0 : undefined}
      onKeyDown={
        isLive ? e => e.key === 'Enter' && handleSelectLive() : undefined
      }
    >
      <div className={styles['match-card__teams']}>
        <TeamRow {...homeTeam} />
        <TeamRow {...awayTeam} />
      </div>

      {isLive && (
        <div
          className={styles['match-card__score']}
          style={{ marginRight: 11 }}
        >
          <span className={styles['match-card__score__gol']}>
            {homeTeam?.score}
          </span>
          <span className={styles['match-card__score__gol']}>
            {awayTeam?.score}
          </span>
        </div>
      )}

      <div className={styles['match-card__info']}>
        {isPending && (
          <>
            <span className={styles['match-card__date']}>
              {renderMaxLengthText(date || '', MAX_DATE)}
            </span>
            <span className={styles['match-card__time']}>{`${time ||
              ''} / ${timezone || ''}`}</span>
          </>
        )}
        {isLive && (
          <>
            {isBroadcastAvailable && (
              <span
                className={styles['match-card__live-badge']}
                style={{
                  backgroundColor: translations?.color_rojo2 || '#EB0045',
                }}
              >
                {getTranslation('tag_envivo', translations)}
              </span>
            )}
            <span
              className={`${styles['match-card__date']} ${
                styles['current-match']
              }`}
            >
              {formatMinute(progress)}
            </span>
          </>
        )}
        {isFinished && (
          <div className={styles['match-card__score']}>
            <span className={styles['match-card__score__gol']}>
              {' '}
              {homeTeam?.score}{' '}
            </span>
            <span className={styles['match-card__score__gol']}>
              {awayTeam?.score}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

MatchCard.propTypes = {
  homeTeam: PropTypes.shape({
    name: PropTypes.string,
    flag: PropTypes.string,
    score: PropTypes.number,
  }),
  awayTeam: PropTypes.shape({
    name: PropTypes.string,
    flag: PropTypes.string,
    score: PropTypes.number,
  }),
  date: PropTypes.string,
  time: PropTypes.string,
  timezone: PropTypes.string,
  status: PropTypes.oneOf(['pending', 'live', 'finished', 'postponed']),
  isWinner: PropTypes.bool,
  isBroadcastAvailable: PropTypes.bool,
  progress: PropTypes.shape({
    period: PropTypes.string,
    minute: PropTypes.string,
    extraMinute: PropTypes.string,
    stoppageMinute: PropTypes.string,
  }),
};

MatchCard.defaultProps = {
  homeTeam: { name: 'Por definir', flag: null, score: null },
  awayTeam: { name: 'Por definir', flag: null, score: null },
  date: '',
  time: '',
  timezone: '',
  status: 'pending',
  isWinner: false,
  isBroadcastAvailable: false,
  progress: null,
};

export default MatchCard;
